import type {
  CopilotProposalStatus,
  CopilotProposalSummary,
} from "@repo/app-types/entities/copilotProposal";
import type { FC } from "react";
import { useLocation, type To } from "react-router";
import { useAppSelector } from "../../hooks/hook.js";
import { readTeammateAgentProposalCardContent } from "../agents/teammateAgentProposalCardContent.js";
import { readAutomationProposalCardContent } from "../automations/automationProposalCardContent.js";
import { readTrainingProposalCardContent } from "../projects/projectSettings/training/trainingProposalCardContent.js";
import { readKnowledgeBaseProposalCardContent } from "../settingsPage/callRecorderSettings/knowledgeBaseProposalCardContent.js";
import { readSignalProposalCardContent } from "../settingsPage/signalSettings/signalProposalCardContent.js";
import { readToolProposalCardContent } from "../tools/toolProposalCardContent.js";
import {
  ConfigurationProposalCard,
  ConfigurationProposalStatus,
  type ConfigurationProposalOperation,
  type ConfigurationProposalResourceType,
} from "./configurationProposalCard.js";
import { useNavigateKeepCopilotChat } from "./copilotPageContext.js";
import { selectEditorProposalStatus } from "./editorProposalsSlice.js";

/**
 * How a pending proposal is reviewed: in the editor that puts it into its
 * draft, where Save applies it, or in place, where the card applies it (see
 * `CopilotProposalCardProps.onReview`) under a button naming the write.
 */
export type CopilotProposalReview =
  | { kind: "open_editor"; location: To }
  | { kind: "apply_in_place"; actionLabel: string };

/** What a resource type contributes to its proposal's card. */
export interface CopilotProposalCardContent {
  resourceType: ConfigurationProposalResourceType;
  operation: ConfigurationProposalOperation;
  /** What the heading calls the thing proposed, where it is a part of the resource. */
  subject?: string;
  /** The name of the thing proposed, where it is a part of the resource; defaults to the resource's. */
  name?: string;
  summary: string;
  highlights: readonly string[];
  review: CopilotProposalReview;
  /** Where the resource the proposal is about is shown once it is resolved. */
  resourceLocation: To & { pathname: string };
}

export interface CopilotProposalCardProps {
  proposal: CopilotProposalSummary<
    | "signal"
    | "automation"
    | "teammateAgent"
    | "training"
    | "knowledgeBaseAttachment"
    | "tool"
  >;
  /** Applies a pending proposal whose content is reviewed in place. */
  onReview?: () => void;
  /** Whether `onReview` is in flight. */
  isSaving?: boolean;
}

/**
 * A copilot proposal in the chat. Review opens the editor with the change in
 * its draft, where Save applies it — or applies the change itself where the
 * resource has no draft; Open shows the resource a resolved one is about,
 * unless it is already open or was never created. While the open page holds
 * or shows the change, the card reads Viewing, then Saving.
 */
export const CopilotProposalCard: FC<CopilotProposalCardProps> = (props) => {
  const { proposal, onReview, isSaving = false } = props;
  const content = readCopilotProposalCardContent(proposal);
  const navigate = useNavigateKeepCopilotChat();
  const { pathname } = useLocation();
  const editorStatus = useAppSelector((state) =>
    selectEditorProposalStatus(state, proposal.id),
  );
  const status = resolveCardStatus(proposal.status, editorStatus, isSaving);
  const action = resolveAction(status, {
    content,
    onReview,
    currentPathname: pathname,
  });

  return (
    <ConfigurationProposalCard
      resourceType={content.resourceType}
      operation={content.operation}
      subject={content.subject}
      resourceName={content.name ?? proposal.resource.name}
      summary={content.summary}
      highlights={content.highlights}
      status={status}
      pendingActionLabel={
        content.review.kind === "apply_in_place"
          ? content.review.actionLabel
          : undefined
      }
      onAction={
        action
          ? () => {
              if ("navigateTo" in action) {
                void navigate(action.navigateTo);
              } else {
                action.review();
              }
            }
          : undefined
      }
    />
  );
};

export function readCopilotProposalCardContent(
  proposal: CopilotProposalCardProps["proposal"],
): CopilotProposalCardContent {
  switch (proposal.resource_type) {
    case "signal":
      return readSignalProposalCardContent(proposal);
    case "automation":
      return readAutomationProposalCardContent(proposal);
    case "teammateAgent":
      return readTeammateAgentProposalCardContent(proposal);
    case "training":
      return readTrainingProposalCardContent(proposal);
    case "knowledgeBaseAttachment":
      return readKnowledgeBaseProposalCardContent(proposal);
    case "tool":
      return readToolProposalCardContent(proposal);
  }
}

/**
 * What the card reads. The open page's report wins while the row is pending
 * or applied: an editor holding a pending proposal in its draft reports it as
 * Viewing, then Saving, and a page showing an applied one reports Viewing.
 * An in-place apply in flight reads Saving. Otherwise the row's stored status.
 */
function resolveCardStatus(
  proposalStatus: CopilotProposalStatus,
  editorStatus: ReturnType<typeof selectEditorProposalStatus>,
  isSaving: boolean,
): ConfigurationProposalStatus {
  switch (proposalStatus) {
    case "pending":
      if (editorStatus) {
        return editorStatus;
      }
      return isSaving
        ? ConfigurationProposalStatus.Saving
        : ConfigurationProposalStatus.Pending;
    case "applied":
      return editorStatus ?? ConfigurationProposalStatus.Applied;
    case "discarded":
    case "stale":
      return ConfigurationProposalStatus.Outdated;
  }
}

type CardAction = { navigateTo: To } | { review: () => void };

function resolveAction(
  status: ConfigurationProposalStatus,
  options: {
    content: CopilotProposalCardContent;
    onReview: (() => void) | undefined;
    currentPathname: string;
  },
): CardAction | undefined {
  const { content, onReview, currentPathname } = options;
  switch (status) {
    case ConfigurationProposalStatus.Pending:
      return resolveReviewAction(content.review, onReview);
    case ConfigurationProposalStatus.Applied:
      return resolveOpenAction(content.resourceLocation, currentPathname);
    case ConfigurationProposalStatus.Outdated:
      // An outdated proposal to create something never created it, so there
      // is nothing to open.
      return content.operation === "create"
        ? undefined
        : resolveOpenAction(content.resourceLocation, currentPathname);
    case ConfigurationProposalStatus.Viewing:
    case ConfigurationProposalStatus.Saving:
      return undefined;
  }
}

/** Opens the resource, unless it is already open. */
function resolveOpenAction(
  resourceLocation: CopilotProposalCardContent["resourceLocation"],
  currentPathname: string,
): CardAction | undefined {
  return resourceLocation.pathname === currentPathname
    ? undefined
    : { navigateTo: resourceLocation };
}

function resolveReviewAction(
  review: CopilotProposalReview,
  onReview: (() => void) | undefined,
): CardAction | undefined {
  switch (review.kind) {
    case "open_editor":
      return { navigateTo: review.location };
    case "apply_in_place":
      return onReview ? { review: onReview } : undefined;
  }
}
